import { DateTime } from 'luxon'
import { parseDate } from '../../helpers/dateHelper'
import { LineItemEntity } from '../../hooks/finance/invoice/useLineItemEntities'

export enum QuantityType {
    Numeric = 'NUMERIC',
    Time = 'TIME',
    Distance = 'DISTANCE',
}

export enum PricePrecedence {
    Subtotal = 'SUBTOTAL',
    Total = 'TOTAL',
    Inherited = 'INHERITED',
}

export type QuantityDto = {
    id: string
    type: QuantityType
    amount: string
    rate: string
    description: string
    date: string
    user: string
}

export type PriceSummaryDto = {
    id?: string
    precedence: PricePrecedence
    currency: string
    subtotal: string
    total: string
}

export type PriceVatDto = {
    id: string
    value: string
    type: string
}

export type PriceDto = PriceSummaryDto & {
    vat: PriceVatDto[]
}

export type BaseTierDto = {
    type: string
    validFrom?: string
    validTo?: string
    salesRate: string
    currency?: string
}

export type BaseUserTierDto = BaseTierDto & {
    userId: string
}

export type PartyTierDto = BaseTierDto & {
    partyId: string
}

export type UserTierDto = BaseUserTierDto & {
    purchaseRate?: string
}

export type UserInsolvencyTierDto = BaseUserTierDto & {
    role: string
    estateRate: string
    factorExperience: string
    factorEstate: string
    factorTotal: string
}

export type SurchargeListItemDto = LineItemEntity & {
    surchargeType: string
    name: string
    value: string
}

export function isBaseUserTier(tier: BaseTierDto): tier is BaseUserTierDto {
    return ['USER_TIER', 'USER_INSOLVENCY_TIER'].includes(tier.type)
}

export function isUserTier(tier: BaseTierDto): tier is UserTierDto {
    return tier.type === 'USER_TIER'
}

export function isUserInsolvencyTier(tier: BaseTierDto): tier is UserInsolvencyTierDto {
    return tier.type === 'USER_INSOLVENCY_TIER'
}

export function isPartyTier(tier: BaseTierDto): tier is PartyTierDto {
    return tier.type === 'PARTY_TIER'
}

export function isActiveTier(tier: BaseTierDto, date?: DateTime): boolean {
    if (!date) {
        date = DateTime.now().startOf('day')
    }
    return (
        (!tier.validFrom && !tier.validTo) ||
        (parseDate(tier.validTo || '3000-01-01')
            .diff(date)
            .toMillis() >= 0 &&
            parseDate(tier.validFrom || '1970-01-01')
                .diff(date)
                .toMillis() <= 0)
    )
}
